import { BrowserDetection } from './browser-detection';

export type UserAgentParserResponse = {
    browser: string;
    browserVersion: string;
    browserVersionNumber: string;
};

export class UserAgentParser {
    private EDGE_BROWSER_NAME = 'Edge';
    private OPERA_BROWSER_NAME = 'Opera';
    private browserDetection: BrowserDetection;

    constructor(private userAgent: string) {
        this.browserDetection = new BrowserDetection(this.userAgent);
    }

    public static getInstance(userAgent: string): UserAgentParser {
        return new this(userAgent);
    }

    public parseUserAgentString(): UserAgentParserResponse {
        const browserPattern = this.getUserAgentBrowserPattern();
        const browser = this.getUserAgentBrowserName(browserPattern);
        const browserVersion = this.parseUserAgentBrowserVersion();
        const browserVersionNumber = this.getUserAgentBrowserVersionNumber();
        return {
            browser,
            browserVersion,
            browserVersionNumber,
        };
    }

    private getUserAgentBrowserName(browserPattern: string): string {
        const namePattern = /[^0-9^\/^.]+/g;
        const matches = this.getRegExpMatch(namePattern, browserPattern);
        return matches[0] ?? 'Unknown';
    }

    private parseUserAgentBrowserVersion(): string {
        const browserPattern = this.getUserAgentBrowserPattern();
        if (this.browserDetection.isSafari()) {
            const userAgentString = this.userAgent;
            const versionPattern = /Version\/(\d+)(.(\d)+)?/g;
            const versionData = this.getRegExpMatch(versionPattern, userAgentString);
            let versionString = versionData[0] ?? '';
            versionString = versionString.toUpperCase();
            const browserName = this.getUserAgentBrowserName(browserPattern);
            return versionString.replace('VERSION/', `${browserName} `);
        }
        return browserPattern.replace('/', ' ');
    }

    private getUserAgentBrowserVersionNumber(): string {
        const browserPattern = this.getUserAgentBrowserPattern();
        return browserPattern.slice(browserPattern.indexOf('/') + 1);
    }

    private getUserAgentBrowserPattern(): string {
        const userAgentString = this.userAgent;
        const paternBrowser = /([^\/^\(^\)^\n]+\/\d+(.\d+)+)[ ]?/g;
        const browserPatterns = this.getRegExpMatch(paternBrowser, userAgentString);
        let browserItem = this.getUserAgentBrowserItem(browserPatterns ?? []);
        browserItem = browserItem.trim();
        browserItem = this.handleBrowserPatternExceptions(browserItem);
        return browserItem;
    }

    private handleBrowserPatternExceptions(browserItem: string) {
        if (this.browserDetection.isEdge()) {
            const edgePattern = /Edg\//;
            return browserItem.replace(edgePattern, `${this.EDGE_BROWSER_NAME}/`);
        } else if (this.browserDetection.isOpera()) {
            const operaPattern = /OPR\//;
            return browserItem.replace(operaPattern, `${this.OPERA_BROWSER_NAME}/`);
        }
        return browserItem;
    }

    private getRegExpMatch(pattern: RegExp, searchString: string): string[] {
        const matches = [...searchString.matchAll(pattern)];
        return matches.map((execArray) => {
            return execArray[0];
        });
    }

    private detectPlatformExceptions(): boolean {
        return (
            this.browserDetection.isSafari() ||
            this.browserDetection.isEdge() ||
            this.browserDetection.isOpera()
        );
    }

    private getUserAgentBrowserItem(patterns: string[]): string {
        if (patterns.length == 0) {
            return '';
        }
        const item = this.detectPlatformExceptions() ? patterns.pop() : patterns[2];
        return item ?? '';
    }
}
